/*
 * A binary mask as run lengths: how the Spore analysis workbench and the
 * Culture analysis page send a painted mask to the server, which reads it
 * with routes/masks.py decode_rle and measures it itself.
 *
 *   {width, height, counts: [zeros, ones, zeros, ones, ...]}
 *
 * Row by row from the top-left pixel (row-major, unlike COCO's column-major
 * RLE), alternating runs that start with a run of 0s (which may be 0 long);
 * the counts add up to width x height. A mask is drawn on the analysis grid
 * (utils/annotation/raster.js analysisSize: at most 1024 px on its long
 * side), with the picture's own proportions, so it covers the whole picture
 * at a known scale: image pixels per mask pixel = imageWidth / width.
 */
export const RLE_LIMITS = Object.freeze({
  //: 2048 x 2048; the analysis grid is at most 1024 on its long side
  maxPixels: 4194304,
  maxCounts: 400000,
});

/** The mask (any array of 0/1, length width x height) as run lengths. */
export function encodeRle(mask, width, height) {
  const n = width * height;
  const counts = [];
  let current = 0;
  let run = 0;
  for (let i = 0; i < n; i += 1) {
    const v = mask[i] ? 1 : 0;
    if (v === current) run += 1;
    else {
      counts.push(run);
      current = v;
      run = 1;
    }
  }
  counts.push(run);
  return { width, height, counts };
}

/** Why these run lengths are not a mask, or null. */
export function rleProblem(rle) {
  if (!rle || typeof rle !== 'object') return 'notAnObject';
  const { width, height, counts } = rle;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1
      || width * height > RLE_LIMITS.maxPixels) return 'badSize';
  if (!Array.isArray(counts) || !counts.length || counts.length > RLE_LIMITS.maxCounts) return 'badCounts';
  let total = 0;
  for (let i = 0; i < counts.length; i += 1) {
    const c = counts[i];
    if (!Number.isInteger(c) || c < 0 || (c === 0 && i > 0)) return 'badCounts';
    total += c;
  }
  return total === width * height ? null : 'badTotal';
}

/** The run lengths back as a Uint8Array mask (1 = in the mask). Throws on a malformed one. */
export function decodeRle(rle) {
  const problem = rleProblem(rle);
  if (problem) throw new Error(`invalid mask: ${problem}`);
  const mask = new Uint8Array(rle.width * rle.height);
  let at = 0;
  rle.counts.forEach((c, i) => {
    if (i % 2) mask.fill(1, at, at + c);
    at += c;
  });
  return mask;
}

/** How many pixels are set. */
export function countMask(mask) {
  let n = 0;
  for (let i = 0; i < mask.length; i += 1) n += mask[i] ? 1 : 0;
  return n;
}
